import {
  CommonDecisionTreeNodeType,
  MacroDecisionTreeActionParams,
  MacroDecisionTreeNodeActions,
} from '@frontapp/workflows/decision_trees';
import {RuleActionType} from '@frontapp/workflows/rules';
import {MoveType} from '@frontapp/workflows/rules/parameters';
import {isNull, isUndefined} from 'lodash';
import React, {ReactNode, useState} from 'react';

import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getMacroInputs} from '../../../../helpers/macroHelpers';
import {MacroModel} from '../../../../models/api/macroModel';
import {DismissedAlertsEnum} from '../../../../models/api/preferencesModel';
import {runMacroAction} from '../../../../store/data/macros/macroActions';
import {updateDismissedAlertsAction} from '../../../../store/data/preferences/preferencesActions';
import {getDismissedAlerts} from '../../../../store/data/preferences/preferencesSelectors';
import {MacroInputDialogContainer} from './macroInput/macroInputDialogContainer';
import {MacroMoveConfirmationDialog} from './macroMoveConfirmationDialog';

export interface UseRunMacroReturn {
  onRunMacro: (macro: MacroModel) => void;
  dialog: ReactNode | null;
}

/*
 * Selectors.
 */

const getHasDismissedWarnOnLoseAccessMacro = createSelector([getDismissedAlerts], (dismissedAlerts) =>
  dismissedAlerts.includes(DismissedAlertsEnum.WARN_ON_LOSE_ACCESS_MACRO),
);

/*
 * Component.
 */

export const useRunMacro = (conversationId: number | undefined): UseRunMacroReturn => {
  const dispatch = useAppDispatch();
  const [macroToAskConfirmation, setMacroToAskConfirmation] = useState<MacroModel>();
  const [macroToAskInputs, setMacroToAskInputs] = useState<MacroModel>();
  const hasDismissedWarnOnLoseAccessMacro = useAppSelector(getHasDismissedWarnOnLoseAccessMacro);
  const onDismissMoveActionWarning = () => {
    dispatch(updateDismissedAlertsAction(DismissedAlertsEnum.WARN_ON_LOSE_ACCESS_MACRO));
  };

  const onRunMacro = (macroToRun: MacroModel) => {
    if (isUndefined(conversationId)) {
      return;
    }

    const macroInputs = getMacroInputs(macroToRun);
    if (macroInputs && macroInputs.length > 0) {
      setMacroToAskInputs(macroToRun);
      return;
    }

    if (!hasDismissedWarnOnLoseAccessMacro && computeHasMacroRestirctedInboxesAction(macroToRun)) {
      setMacroToAskConfirmation(macroToRun);
      return;
    }

    dispatch(runMacroAction(macroToRun, conversationId));
  };

  const renderDialog = () => {
    if (!isDefined(conversationId)) {
      return null;
    }

    if (isDefined(macroToAskInputs)) {
      return (
        <MacroInputDialogContainer
          macro={macroToAskInputs}
          onRequestClose={() => setMacroToAskInputs(undefined)}
          onOkClick={(macroInputData) => {
            dispatch(runMacroAction(macroToAskInputs, conversationId, macroInputData));
            setMacroToAskInputs(undefined);
          }}
          shouldShowMoveActionWarning={
            !hasDismissedWarnOnLoseAccessMacro && computeHasMacroRestirctedInboxesAction(macroToAskInputs)
          }
          onDismissMoveActionWarning={onDismissMoveActionWarning}
        />
      );
    }

    if (isDefined(macroToAskConfirmation)) {
      return (
        <MacroMoveConfirmationDialog
          onOkClick={() => {
            dispatch(runMacroAction(macroToAskConfirmation, conversationId));
            setMacroToAskConfirmation(undefined);
          }}
          onRequestClose={() => {
            setMacroToAskConfirmation(undefined);
          }}
          macroName={macroToAskConfirmation.name}
          macroId={macroToAskConfirmation.id}
          onDismissMoveActionWarning={onDismissMoveActionWarning}
        />
      );
    }

    return null;
  };

  const dialog = renderDialog();

  return {
    onRunMacro,
    dialog,
  };
};

function isMacroDecisionTreeNodeActions(node: any): node is MacroDecisionTreeNodeActions {
  return node.type === CommonDecisionTreeNodeType.ACTION;
}

function isMoveAction(action: {
  type: string;
}): action is {type: RuleActionType.MOVE; params: MacroDecisionTreeActionParams[RuleActionType.MOVE]} {
  return action.type === RuleActionType.MOVE;
}

function computeHasMacroRestirctedInboxesAction(macro: MacroModel) {
  if (isDefined(macro.actions) && macro.actions.length > 0) {
    return macro.actions.some(
      (action) => isMoveAction(action) && action.params.moveType === MoveType.RESTRICT,
    );
  }

  if (isUndefined(macro.decisionTree) || isNull(macro.decisionTree.tree_data)) {
    return false;
  }

  return macro.decisionTree.tree_data.nodes
    .map((node) => node.data)
    .filter(isMacroDecisionTreeNodeActions)
    .flatMap((data) => data.actions)
    .some((action) => isMoveAction(action) && action.params.moveType === MoveType.RESTRICT);
}
